<script lang="ts">
  import { defineComponent, nextTick, ref, watch } from 'vue'
  import { TriangleAlert } from 'lucide-vue'
  import Button from '@/components/Button.vue'
  import { useSession } from '@/composables/useSession'

  export default defineComponent({
    name: 'SessionExpiryDialog',

    components: { Button, IconWarning: TriangleAlert },

    setup() {
      const { isWarning, isRenewing, secondsRemaining, renew } = useSession()
      const dialog = ref<HTMLElement | null>(null)

      // Interrupting is the point, so the dialog takes focus. Queried rather than
      // reffed: a ref on <Button> yields the component instance, which has no focus().
      watch(isWarning, (showing) => {
        if (showing) {
          nextTick(() => dialog.value?.querySelector('button')?.focus())
        }
      })

      // A modal with nowhere else to go: Tab has to wrap inside it, or focus
      // walks off into the form underneath that the user cannot reach.
      const trapFocus = (event: KeyboardEvent) => {
        const focusable = dialog.value?.querySelectorAll<HTMLElement>(
          'button:not([disabled])',
        )

        if (!focusable?.length) return

        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }

      const minutes = (seconds: number) => Math.floor(seconds / 60)
      const seconds = (value: number) => String(value % 60).padStart(2, '0')

      return {
        isWarning,
        isRenewing,
        secondsRemaining,
        renew,
        dialog,
        trapFocus,
        minutes,
        seconds,
      }
    },
  })
</script>

<template>
  <div v-if="isWarning" class="session-expiry" @keydown.tab="trapFocus">
    <div
      ref="dialog"
      class="session-expiry__dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expiry-title"
    >
      <IconWarning class="session-expiry__icon" />

      <h2 id="session-expiry-title">
        {{ $t('session.warningTitle') }}
      </h2>

      <p class="session-expiry__body">
        {{ $t('session.warningBody') }}
      </p>

      <p class="session-expiry__countdown" aria-live="polite">
        {{ minutes(secondsRemaining || 0) }}:{{
          seconds(secondsRemaining || 0)
        }}
      </p>

      <Button :loading="isRenewing" @click="renew">
        {{ $t('session.staySignedIn') }}
      </Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '@/variables' as *;

  .session-expiry {
    position: fixed;
    inset: 0;
    z-index: 90010;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $space-5;
    background-color: rgba($neutral-900, 0.45);

    &__dialog {
      width: 100%;
      max-width: 22rem;
      padding: $space-6;
      border-radius: $radius-lg;
      background-color: $surface;
      box-shadow: $shadow-md;
      text-align: center;

      h2 {
        margin: 0 0 $space-2 0;
        font-size: $font-size-lg;
      }
    }

    &__icon {
      width: 2rem;
      height: 2rem;
      margin-bottom: $space-3;
      color: $color-warning;
    }

    &__body {
      margin: 0 0 $space-4 0;
      color: $text-muted;
      font-size: $font-size-sm;
    }

    &__countdown {
      margin: 0 0 $space-5 0;
      font-size: $font-size-2xl;
      font-weight: $font-weight-bold;
      font-variant-numeric: tabular-nums;
      line-height: 1;
    }

    :deep(.button) {
      width: 100%;
    }
  }
</style>
